import React, { useCallback, useEffect, useId, useState } from 'react';

import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useSpinWheel } from '../contexts/SpinWheelContext';
import { t } from '../i18n/translator';
import styles from '../styles/components/spin-wheel-popup.module.scss';
import { formatPaymentAmount } from '../utils/amount';

import SiteLogo from './SiteLogo';
import SpinWheelCanvas from './spin-wheel/SpinWheelCanvas';
import { visualWheelSegments } from './spin-wheel/spinWheelShared';
import WildMarketModal from './spin-wheel/WildMarketModal';
import WildProgressBar from './spin-wheel/WildProgressBar';
import TasksWidget from './TasksWidget';
import { useToast } from './Toast';
import Modal from './ui/Modal/Modal';

const PROMO_CODE_MAX = 32;
const FEEDBACK_AUTO_DISMISS_MS = 6000;

const SpinWheelPopup: React.FC = () => {
  const wheel = useSpinWheel();
  const { settings } = useSiteSettings();
  const { showToast, removeToast } = useToast();
  const feedbackId = useId();
  const errorToastId = `spin-wheel-error-${feedbackId}`;
  const successToastId = `spin-wheel-success-${feedbackId}`;
  const [marketOpen, setMarketOpen] = useState(false);
  const openMarket = useCallback(() => setMarketOpen(true), []);
  const closeMarket = useCallback(() => setMarketOpen(false), []);

  // Briefly highlight the Wild progress bar after an authoritative Wild win.
  const [wildIncoming, setWildIncoming] = useState(false);

  // Feedback banner'ı (başarı/hata) 6 saniye sonra otomatik temizle.
  // Yeni bildirim geldiğinde timer resetlenir; kapatıldığında cleanup devreye
  // girer. `wheel` null olduğunda hiçbir şey yapılmaz.
  const uiError = wheel?.uiError ?? null;
  const uiSuccess = wheel?.uiSuccess ?? null;
  const feedbackVisible = wheel?.isOpen ?? false;
  const setUiError = wheel?.setUiError;
  const setUiSuccess = wheel?.setUiSuccess;

  useEffect(() => {
    if (!uiError && !uiSuccess) return;
    if (!setUiError || !setUiSuccess) return;
    const timer = window.setTimeout(() => {
      setUiError(null);
      setUiSuccess(null);
    }, FEEDBACK_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [uiError, uiSuccess, setUiError, setUiSuccess]);

  const lastWonRewardType = wheel?.lastWonSegment?.rewardType;
  const lastWonId = wheel?.lastWonSegment?.id;
  useEffect(() => {
    if (lastWonRewardType !== 'wild') return;
    setWildIncoming(true);
    const timer = window.setTimeout(() => setWildIncoming(false), 1600);
    return () => window.clearTimeout(timer);
  }, [lastWonRewardType, lastWonId]);

  const dismissFeedback = useCallback(() => {
    if (!setUiError || !setUiSuccess) return;
    setUiError(null);
    setUiSuccess(null);
  }, [setUiError, setUiSuccess]);

  useEffect(() => {
    // The shared toast follows the existing feedback state and its 6-second
    // timer; closing it manually clears that state through the same handler.
    if (feedbackVisible && uiError) {
      showToast('error', uiError, 0, { id: errorToastId, onDismiss: dismissFeedback });
    } else {
      removeToast(errorToastId);
    }
    if (feedbackVisible && uiSuccess) {
      showToast('success', uiSuccess, 0, { id: successToastId, onDismiss: dismissFeedback });
    } else {
      removeToast(successToastId);
    }

    return () => {
      removeToast(errorToastId);
      removeToast(successToastId);
    };
  }, [feedbackVisible, uiError, uiSuccess, errorToastId, successToastId, showToast, removeToast, dismissFeedback]);

  const handleWheelClick = useCallback(() => {
    if (!wheel) {
      return;
    }

    const segmentsOnWheel = visualWheelSegments(wheel.status.segments || []);
    const canSpin =
      !wheel.isSpinAnimating &&
      !wheel.spinMutation.isPending &&
      wheel.status.availableCredits > 0 &&
      segmentsOnWheel.length > 0;

    if (!canSpin) return;
    wheel.spinMutation.mutate();
  }, [wheel]);

  const handleTaskFeedback = useCallback(
    (feedback: { type: 'success' | 'error'; message: string } | null) => {
      if (!wheel) {
        return;
      }

      if (!feedback) {
        wheel.setUiError(null);
        wheel.setUiSuccess(null);
        return;
      }

      if (feedback.type === 'error') {
        wheel.setUiSuccess(null);
        wheel.setUiError(feedback.message);
      } else {
        wheel.setUiError(null);
        wheel.setUiSuccess(feedback.message);
      }
    },
    [wheel]
  );

  if (!wheel) {
    return null;
  }

  const {
    userKey,
    status,
    isOpen,
    handleClose,
    code,
    setCode,
    lastWonSegment,
    isSpinAnimating,
    spinMutation,
    redeemMutation,
    invalidateSpinData,
    handleCodeSubmit,
    handleSpinSettled,
    wheelRef,
    wildStatus,
    wildLoading,
    wildRedeemMutation,
  } = wheel;

  const hasWildSegment = (status.segments || []).some((segment) => segment.rewardType === 'wild');
  const wildReady = wildStatus !== null;
  const wildBalance = wildStatus?.balance ?? 0;
  const wildCatalog = wildStatus?.catalog ?? [];
  const currency = settings.currency;

  const segmentsOnWheel = visualWheelSegments(status.segments || []);
  const canSpin = !isSpinAnimating && !spinMutation.isPending && status.availableCredits > 0 && segmentsOnWheel.length > 0;

  const hasGlobalDepositRules =
    status.progress.requiredDepositCount > 0 || status.progress.requiredDepositAmount > 0;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t(2304)}
      titleContent={<SiteLogo className={styles.brandLogo} />}
      size="lg"
      className={styles.modal}
      overlayClassName={styles.overlay}
    >
      <div className={styles.content}>
        <div className={`${styles.hero} ${hasWildSegment ? styles.heroWithWild : ''}`}>
          <div className={styles.tasksColumn}>
            <div className={styles.tasksPanel}>
              <div className={styles.tasksSection}>
                <TasksWidget
                  userKey={userKey}
                  onAfterClaim={invalidateSpinData}
                  hideWhenEmpty={false}
                  title={t(2441)}
                  onFeedback={handleTaskFeedback}
                  appearance="wheel"
                />
              </div>
              {hasGlobalDepositRules ? (
                <div className={styles.globalRulesBlock}>
                  <div className={styles.progressHeader}>
                    <h4>{t(2442)}</h4>
                    <span>{t(2443)}</span>
                  </div>
                  <div className={styles.progressGrid}>
                    <div>
                      <span>{t(2444)}</span>
                      <strong>
                        {status.progress.depositCount} /{' '}
                        {status.progress.requiredDepositCount > 0
                          ? status.progress.requiredDepositCount
                          : '—'}
                      </strong>
                    </div>
                    <div>
                      <span>{t(2445)}</span>
                      <strong>
                        {formatPaymentAmount(status.progress.depositAmount, currency)} /{' '}
                        {status.progress.requiredDepositAmount > 0
                          ? formatPaymentAmount(status.progress.requiredDepositAmount, currency)
                          : '—'}
                      </strong>
                    </div>
                  </div>
                  {status.progress.goalMet ? (
                    <p className={styles.progressFootnote}>{t(2446)}</p>
                  ) : null}
                </div>
              ) : null}
            </div>

            <aside className={styles.promoColumn}>
              <input
                className={styles.promoColumnInput}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder={t(2448)}
                maxLength={PROMO_CODE_MAX}
                spellCheck={false}
                autoCapitalize="characters"
                autoCorrect="off"
                aria-label={t(2448)}
              />
              <button
                type="button"
                className={styles.promoColumnButton}
                onClick={handleCodeSubmit}
                disabled={redeemMutation.isPending}
              >
                {redeemMutation.isPending ? t(2519) : t(2520)}
              </button>
            </aside>
          </div>

          <div className={styles.wheelGroup}>
            {hasWildSegment ? (
              <WildProgressBar
                balance={wildBalance}
                catalog={wildCatalog}
                onOpenMarket={openMarket}
                wildIncoming={wildIncoming}
                available={wildReady}
                loading={wildLoading}
              />
            ) : null}

            <div className={styles.wheelColumn}>
              <div className={styles.stageSection}>
                <div
                  className={[
                    styles.stage,
                    canSpin ? styles.stageReady : '',
                    isSpinAnimating ? styles.stageSpinning : '',
                    lastWonSegment ? styles.stageWon : '',
                  ].filter(Boolean).join(' ')}
                  onClick={handleWheelClick}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleWheelClick();
                    }
                  }}
                  role="button"
                  tabIndex={canSpin ? 0 : -1}
                  aria-label={canSpin ? t(2521) : t(2522)}
                  aria-disabled={!canSpin}
                >
                  <SpinWheelCanvas
                    ref={wheelRef}
                    segments={status.segments || []}
                    size={600}
                    className={styles.wheelCanvas}
                    onSettle={handleSpinSettled}
                    idleSpin={isOpen && !isSpinAnimating && !spinMutation.isPending && !lastWonSegment}
                  />
                  {isSpinAnimating && (
                    <div className={styles.wheelSpinning} aria-hidden="true">{t(2449)}</div>
                  )}
                </div>

                <div className={styles.pointer} aria-hidden="true" />

              </div>
              <button
                type="button"
                className={styles.spinButton}
                onClick={handleWheelClick}
                disabled={!canSpin}
              >
                <span aria-live="polite" aria-atomic="true">
                  {isSpinAnimating || spinMutation.isPending
                    ? t(2449)
                    : canSpin
                      ? t(2765, { values: { credits: status.availableCredits } })
                      : t(2524)}
                </span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </Modal>

    {/* Market modal ana popup'un kardeşi olarak render edilir — iç içe modal
        istemiyoruz. `hasWildSegment` false ise hiç mount edilmez; true ise
        `marketOpen` state'ine göre görünür/gizli. */}
    {hasWildSegment && wildReady ? (
      <WildMarketModal
        isOpen={marketOpen}
        onClose={closeMarket}
        balance={wildBalance}
        catalog={wildCatalog}
        onRedeem={(rewardId) => wildRedeemMutation.mutate(rewardId)}
        isRedeeming={wildRedeemMutation.isPending}
      />
    ) : null}
    </>
  );
};

export default SpinWheelPopup;
